import { Link } from "react-router-dom";
import { useState } from "react";
import { ShoppingBag, User as UserIcon, LogOut, LayoutDashboard, Package, Menu, Heart } from "lucide-react";
import { CategoryDrawer } from "./CategoryDrawer";
import defaultLogo from "@/assets/rup-shoili-logo.jpg";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/store/cart";
import { useSettings } from "@/hooks/useSettings";
import { useWishlist } from "@/hooks/useWishlist";
import { Button } from "@/components/ui/button";
import { PremiumSearchBar } from "@/components/store/PremiumSearchBar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useNavigate } from "react-router-dom";

export const StoreHeader = () => {
  const { user, isAdmin, signOut } = useAuth();
  const { count, setOpen } = useCart();
  const settings = useSettings();
  const { count: wishCount } = useWishlist();
  const navigate = useNavigate();
  const itemCount = count();
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <header className="sticky top-0 z-40 w-full max-w-[100vw] overflow-x-hidden border-b border-border/60 bg-background/85 backdrop-blur-lg">
      <div className="container flex h-16 items-center gap-1 md:gap-4 min-w-0">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setDrawerOpen(true)}
          className="gap-1.5 px-1.5 md:px-3 shrink-0"
          aria-label="Open categories menu"
        >
          <Menu className="h-5 w-5" />
          <span className="hidden sm:inline text-sm font-medium">All</span>
        </Button>
        <CategoryDrawer open={drawerOpen} onOpenChange={setDrawerOpen} />
        <Link to="/" className="flex items-center gap-2 shrink min-w-0">
          <img
            src={settings.logo_url || defaultLogo}
            alt={`${settings.store_name || "Rupshoili"} logo`}
            className="h-9 w-9 md:h-10 md:w-10 rounded-full object-cover shadow-sm bg-background shrink-0"
          />
          <span className="font-display text-lg md:text-2xl font-bold tracking-tight truncate">
            {settings.store_name || "Rupshoili"}
          </span>
        </Link>

        <div className="hidden md:flex flex-1 max-w-md">
          <PremiumSearchBar variant="header" />
        </div>

        <div className="flex items-center gap-0.5 sm:gap-2 ml-auto shrink-0">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="rounded-full h-9 w-9 sm:h-10 sm:w-10">
                  <UserIcon className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <div className="px-2 py-1.5 text-xs text-muted-foreground truncate">{user.email}</div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/orders")}>
                  <Package className="mr-2 h-4 w-4" /> My orders
                </DropdownMenuItem>
                {isAdmin && (
                  <DropdownMenuItem onClick={() => navigate("/admin")}>
                    <LayoutDashboard className="mr-2 h-4 w-4" /> Admin panel
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={signOut}>
                  <LogOut className="mr-2 h-4 w-4" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button variant="ghost" size="sm" className="px-2 sm:px-3" onClick={() => navigate("/auth")}>
              <UserIcon className="h-5 w-5 sm:hidden" />
              <span className="hidden sm:inline">Sign in</span>
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="rounded-full relative h-9 w-9 sm:h-10 sm:w-10"
            onClick={() => navigate("/wishlist")}
            aria-label="Open wishlist"
          >
            <Heart className={wishCount > 0 ? "h-5 w-5 fill-destructive text-destructive" : "h-5 w-5"} />
            {wishCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 grid h-5 w-5 place-items-center rounded-full bg-destructive text-[10px] font-bold text-destructive-foreground">
                {wishCount}
              </span>
            )}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="rounded-full relative h-9 w-9 sm:h-10 sm:w-10"
            onClick={() => setOpen(true)}
            aria-label="Open cart"
          >
            <ShoppingBag className="h-5 w-5" />
            {itemCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 grid h-5 w-5 place-items-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground">
                {itemCount}
              </span>
            )}
          </Button>
        </div>
      </div>
      <div className="md:hidden container pb-3">
        <PremiumSearchBar variant="mobile" />
      </div>
    </header>
  );
};
